import { HealthSystemDomainDeviceResultReaderViewModel } from '../../api/HealthSystemApi';
import { getReaderStatusByAge, needsAttention, ReaderStatus } from '../../utils/readerStatus';
import { readerStatusColors, slaughterhouseColor } from './colors';

export interface ReaderRow {
  reader: HealthSystemDomainDeviceResultReaderViewModel;
  status: ReaderStatus;
  slaughterhouseName: string;
  /** The colour of this device's slaughterhouse slice, so the row swatch and the donut arc agree. */
  slaughterhouseColor: string;
}

export interface SlaughterhouseSlice {
  name: string;
  color: string;
  total: number;
  needingAttention: number;
}

export interface StatusSlice {
  status: ReaderStatus;
  count: number;
  color: string;
}

export interface ReadersSummary {
  total: number;
  healthy: number;
  needingAttention: number;
  /** Whole percent of devices heard from recently. 0 when there are no devices, and the donut draws empty. */
  healthyPercent: number;
  statuses: StatusSlice[];
  /** The slaughterhouses the legend names and the donut colours, biggest first. */
  slaughterhouses: SlaughterhouseSlice[];
  /**
   * Everything past the legend, pooled into one grey slice - null when it all fits. Recycling the indigo
   * scale instead would put the same colour on two named arcs, which makes the legend wrong rather than short.
   */
  otherSlaughterhouses: (SlaughterhouseSlice & { siteCount: number }) | null;
  rows: ReaderRow[];
}

const UNASSIGNED = '—';

/** Beyond this the legend stops being a legend and becomes a second list. */
export const DEFAULT_NAMED_SLICE_LIMIT = 5;

/** Worst first: a page of green rows with one red at the bottom hides the only row worth reading. */
const severity: Record<ReaderStatus, number> = { down: 0, stale: 1, healthy: 2 };

/**
 * Rolls a page of readers into the two donuts, the legends and the drill-down list in one pass.
 *
 * Pure on purpose - the widget hands it whatever the device endpoint returned and renders the result, so the
 * counts under the donuts and the rows in the list can never be computed from different snapshots.
 */
export const summarizeReaders = (
  readers: HealthSystemDomainDeviceResultReaderViewModel[],
  namedSliceLimit: number = DEFAULT_NAMED_SLICE_LIMIT
): ReadersSummary => {
  const statusOf = new Map<string, ReaderStatus>();
  const perSlaughterhouse = new Map<string, { total: number; needingAttention: number }>();

  readers.forEach((reader) => {
    const status = getReaderStatusByAge(reader.lastHeartbeatAt);
    statusOf.set(reader.id, status);

    const name = reader.slaughterhouse?.name?.trim() || UNASSIGNED;
    const bucket = perSlaughterhouse.get(name) ?? { total: 0, needingAttention: 0 };
    bucket.total += 1;
    if (needsAttention(status)) bucket.needingAttention += 1;
    perSlaughterhouse.set(name, bucket);
  });

  const counts: Record<ReaderStatus, number> = { healthy: 0, stale: 0, down: 0 };
  statusOf.forEach((status) => {
    counts[status] += 1;
  });

  // Biggest first, so the colour steps run dark-to-light around the ring and ties stay alphabetical rather
  // than following whatever order the API happened to page in.
  const ranked = Array.from(perSlaughterhouse.entries())
    .map(([name, bucket]) => ({ name, ...bucket }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));

  const slaughterhouses: SlaughterhouseSlice[] = ranked
    .slice(0, namedSliceLimit)
    .map((slice, index) => ({ ...slice, color: slaughterhouseColor(index) }));

  const spare = ranked.slice(namedSliceLimit);
  const otherSlaughterhouses = spare.length
    ? {
        name: '',
        color: readerStatusColors.other,
        siteCount: spare.length,
        total: spare.reduce((sum, slice) => sum + slice.total, 0),
        needingAttention: spare.reduce((sum, slice) => sum + slice.needingAttention, 0)
      }
    : null;

  const colorOf = new Map(slaughterhouses.map((slice) => [slice.name, slice.color]));

  const rows: ReaderRow[] = readers
    .map((reader) => {
      const slaughterhouseName = reader.slaughterhouse?.name?.trim() || UNASSIGNED;
      return {
        reader,
        status: statusOf.get(reader.id) ?? 'down',
        slaughterhouseName,
        // Rows from an unnamed slaughterhouse take the pooled grey, so the swatch never claims a legend
        // colour that belongs to a different site.
        slaughterhouseColor: colorOf.get(slaughterhouseName) ?? readerStatusColors.other
      };
    })
    // Grouped by slaughterhouse first - that is how the estate is walked - then worst device first inside it.
    .sort(
      (a, b) =>
        a.slaughterhouseName.localeCompare(b.slaughterhouseName) ||
        severity[a.status] - severity[b.status] ||
        a.reader.macAddress.localeCompare(b.reader.macAddress)
    );

  const total = readers.length;
  const needingAttentionTotal = counts.stale + counts.down;

  return {
    total,
    healthy: counts.healthy,
    needingAttention: needingAttentionTotal,
    healthyPercent: total === 0 ? 0 : Math.round((counts.healthy / total) * 100),
    statuses: [
      { status: 'healthy', count: counts.healthy, color: readerStatusColors.healthy },
      { status: 'stale', count: counts.stale, color: readerStatusColors.stale },
      { status: 'down', count: counts.down, color: readerStatusColors.down }
    ],
    slaughterhouses,
    otherSlaughterhouses,
    rows
  };
};
